import { useState } from 'react';
import FaIcon from '@/components/common/FaIcon';
import { IconButton } from '@/components/common/Button';
import { useTrialStatus } from '@/hooks/useTrialStatus';

const CONTACT_SALES = 'mailto:hello@cmd-bridge.com?subject=Command%20Bridge%20trial';

/**
 * Slim full-width trial-status bar rendered above the page content for
 * trial tenants. Severity: info (>7 days left), amber (≤7 days), red
 * (expired — not dismissible, since the tenant is read-only).
 */
export default function TrialBanner() {
  const { isTrial, isExpired, daysRemaining } = useTrialStatus();
  const [dismissed, setDismissed] = useState(false);

  if (!isTrial) return null;
  if (dismissed && !isExpired) return null;

  // 7-day amber window — proportionate warning for a 90-day trial
  const urgent = !isExpired && daysRemaining !== null && daysRemaining <= 7;

  const styles = isExpired
    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
    : urgent
      ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200'
      : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800 text-primary-800 dark:text-primary-200';

  return (
    <div className={`border-b px-4 py-1.5 text-sm flex items-center gap-2 ${styles}`} role="status">
      <FaIcon icon={isExpired ? 'circle-exclamation' : 'clock'} className="h-3.5 w-3.5 flex-shrink-0" />
      <span className="flex-1 min-w-0 truncate">
        {isExpired ? (
          <>Trial expired. Your data is preserved — contact sales to keep working.</>
        ) : (
          <>
            Trial — {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
          </>
        )}
      </span>
      {(isExpired || urgent) && (
        <a href={CONTACT_SALES} className="font-medium underline hover:no-underline flex-shrink-0">
          Contact sales
        </a>
      )}
      {!isExpired && (
        <IconButton
          icon={<FaIcon icon="xmark" className="h-3 w-3" />}
          label="Dismiss trial banner"
          variant="ghost"
          size="xs"
          className="flex-shrink-0"
          onClick={() => setDismissed(true)}
        />
      )}
    </div>
  );
}
